import { ClaimCandidateButton } from "@/components/priorities/ClaimCandidateButton";
import { useRef, useState } from "react";
import { FileText, Loader2, MessageSquare, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSetOpenEntity } from "@/components/copilot/context/CopilotEntityContext";
import { ignoreCopilotInteractOutside } from "@/components/copilot/context/copilotOutsideInteraction";
import { useLivePriority } from "@/hooks/useLivePriority";
import { usePriorityEditPermission } from "@/hooks/usePriorityEditPermission";
import { cn } from "@/lib/utils";
import { Priority } from "@/types";
import { PriorityForm } from "./PriorityForm";
import { PriorityCommentSection } from "./PriorityCommentSection";
import { PromoteQueueToFocusDialog } from "./promote-queue-to-focus-dialog";
import { priorityFormKey, submitLabels, weekYearForPriorityDialog } from "./priorityUtils";
import type { SubmitIntent } from "./form/priority-form-constants";

interface PriorityDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The priority to edit, `null` for create mode. May be stale — the dialog re-reads it. */
  priority: Priority | null;
}

/**
 * Create/Edit priority dialog with an inline comments tab.
 * Shared by the Priorities tab and the My OneThing page so the card → pop-up
 * experience stays identical across both.
 */
export const PriorityDetailDialog = ({ open, onOpenChange, priority: seed }: PriorityDetailDialogProps) => {
  // Every consumer hands over a list row, so the live read lives here rather than in each of them.
  const priority = useLivePriority(seed);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  // Set only when the save just stalled the current user's own Focus row — carries the pid so the
  // promotion prompt can query that user's Queue. Independent of `open`, so it can outlive this
  // dialog closing after a successful save.
  const [promoteUserPid, setPromoteUserPid] = useState<string | null>(null);
  const formSubmitRef = useRef<((intent?: SubmitIntent) => void) | null>(null);

  // Reports the open priority to the copilot's ambient context.
  // Must stay above any conditional return (rules of hooks).
  const dri = priority?.assignments?.find((a) => a.isDri)?.assignedUser ?? priority?.dri;
  useSetOpenEntity(
    open && priority
      ? {
          description: "Open priority detail",
          value: `Priority ${priority.code} — ${priority.name} (${priority.status}${dri ? `, DRI: ${dri.fullName}` : ""})`,
        }
      : null,
  );

  const weekYear = weekYearForPriorityDialog(priority);

  // Mirrors the form's own gating so the footer button says what saving will actually do:
  // a non-owner with their own row saves that row only, not the whole task.
  const { canEditTask, canEditOwnRow } = usePriorityEditPermission(priority ?? undefined);
  const isOwnRowOnly = Boolean(priority) && !canEditTask && canEditOwnRow;
  const labels = submitLabels(priority, isOwnRowOnly);
  const secondary = labels.secondary;

  const close = () => {
    onOpenChange(false);
    setIsFormSubmitting(false);
    formSubmitRef.current = null;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={ignoreCopilotInteractOutside}
        className={cn(
          "flex flex-col max-h-[90vh] bg-[#f8f7f4] p-0 transition-[max-width] duration-300 ease-in-out dark:bg-background",
          priority ? "sm:max-w-[1000px]" : "sm:max-w-[900px]",
        )}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xl">
            <span className="flex items-center gap-2">
              {priority ? (
                <>
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  Edit Priority
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-primary" />
                  Add New Priority
                </>
              )}
            </span>
            {priority?.isCandidate && (
              <span className="shrink-0 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 font-sans text-xs font-normal text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                Candidate
              </span>
            )}
            {priority && <ClaimCandidateButton priority={priority} className="h-7 font-sans text-xs font-normal" />}
            <span className="shrink-0 rounded-md border border-border bg-muted/50 px-2 py-1 text-xs font-normal font-sans text-muted-foreground">
              W{weekYear.week} · {weekYear.year}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            {priority ? `Editing: ${priority.name}` : "Fill in the details to create a new priority."}
          </DialogDescription>
        </DialogHeader>

        <div className="relative z-0 min-h-0 flex-1 overflow-y-auto">
          <div className="px-4 py-0">
            <PriorityForm
              // Remount whenever the server row changes, so a claim made from the header above resets
              // the assignments field array instead of being overwritten by the next save.
              key={priorityFormKey(priority)}
              initialData={priority ?? undefined}
              hideButtons={true}
              onSubmitRef={(submitFn) => {
                formSubmitRef.current = submitFn;
              }}
              onSubmittingChange={setIsFormSubmitting}
              onSuccess={close}
              onCancel={close}
              onOwnFocusStalled={setPromoteUserPid}
            />
          </div>

          {priority ? (
            <Tabs defaultValue="comments" className="border-t">
              <TabsList className="mx-6 mt-2 h-auto w-auto shrink-0 self-end gap-0 bg-transparent p-0">
                <TabsTrigger
                  value="comments"
                  className="group gap-2 rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 py-2 text-sm font-medium text-muted-foreground shadow-none ring-offset-background transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-data-[state=active]:text-foreground" />
                  Comments
                  {commentCount > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                      {commentCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="comments" className="mt-0 px-6 pb-6">
                <PriorityCommentSection
                  priorityPid={priority.pid}
                  enabled={open}
                  onCountChange={setCommentCount}
                />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        <DialogFooter className="relative z-[51] shrink-0 border-t bg-inherit px-6 py-4">
          <Button type="button" variant="outline" onClick={close} disabled={isFormSubmitting}>
            Cancel
          </Button>
          {secondary && (
            <Button type="button" variant="secondary" onClick={() => formSubmitRef.current?.(secondary.intent)} disabled={isFormSubmitting}>
              {secondary.label}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => formSubmitRef.current?.(labels.primary.intent)}
            disabled={isFormSubmitting || (Boolean(priority) && !canEditTask && !canEditOwnRow)}
          >
            {isFormSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {labels.primary.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Sibling of the Dialog root above, not nested inside it — a nested Radix Dialog.Root can fight
        the outer one for focus-restore when `close()` unmounts the form above on save, so this must
        survive as its own independent root to keep showing the prompt after the edit dialog closes. */}
    {promoteUserPid && (
      <PromoteQueueToFocusDialog
        open={Boolean(promoteUserPid)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPromoteUserPid(null);
        }}
        userPid={promoteUserPid}
      />
    )}
    </>
  );
};
